import { useEffect, useRef } from "react";
import { useEditorStore } from "@/store/editorStore";
import { useVariablesStore } from "@/store/variablesStore";

/**
 * Hook that watches for CSS variable changes and triggers a callback
 * This is useful for regenerating hotspots when CSS variables change
 * Now uses combined craftspace + project variables
 */
export const useCSSVariableWatcher = (
  projectId: string | undefined,
  onVariableChange: () => void,
) => {
  const { getCombinedVariables } = useVariablesStore();
  const { project } = useEditorStore();
  const lastVariablesRef = useRef<string>("");
  const lastCSSVariablesRef = useRef<string>("");

  useEffect(() => {
    if (!projectId || !project?.craftspace_id) return;

    const checkForVariableChanges = () => {
      // Check Zustand store variables (combined craftspace + project)
      const variables = getCombinedVariables(project.craftspace_id, projectId);
      const variablesString = JSON.stringify(variables);

      // Check actual CSS variables in the DOM
      const cssVariables: Record<string, string> = {};
      if (typeof window !== "undefined") {
        const computedStyle = getComputedStyle(document.documentElement);
        // Get all CSS custom properties that start with --
        for (let i = 0; i < computedStyle.length; i++) {
          const prop = computedStyle[i];
          if (prop.startsWith("--")) {
            cssVariables[prop] = computedStyle.getPropertyValue(prop).trim();
          }
        }
      }
      const cssVariablesString = JSON.stringify(cssVariables);

      // Check if either store variables or CSS variables changed
      const storeChanged = lastVariablesRef.current && lastVariablesRef.current !== variablesString;
      const cssChanged =
        lastCSSVariablesRef.current && lastCSSVariablesRef.current !== cssVariablesString;

      if (storeChanged || cssChanged) {
        console.log("🔍 CSS Variable change detected:", { storeChanged, cssChanged, cssVariables });
        // Variables have changed, trigger callback
        onVariableChange();
      }

      lastVariablesRef.current = variablesString;
      lastCSSVariablesRef.current = cssVariablesString;
    };

    // Check for changes every 200ms (faster response)
    const interval = setInterval(checkForVariableChanges, 200);

    // Initial check
    checkForVariableChanges();

    return () => {
      clearInterval(interval);
    };
  }, [projectId, project?.craftspace_id, getCombinedVariables, onVariableChange]);
};
